import {
  IconBrandFacebookFilled,
  IconBrandInstagram,
  IconBrandTiktok,
} from "@tabler/icons-react";
import Image from "next/image";
import { TermsAndPoliciesLinks } from "./TermsAndPoliciesLinks";
import { useSettings } from "@/libs/hooks/useSettings";

export const Footer = () => {
  const { settings } = useSettings();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden">
      {/* Vintage espresso background */}
      <div className="absolute inset-0 bg-gradient-to-b from-vintage-espresso via-[#231510] to-[#1A0F0A]">
        {/* Subtle pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-ochre/50 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-16">
        {/* Main content */}
        <div className="flex flex-col items-center">
          {/* Logo with decorative frame */}
          <div className="relative mb-8">
            <div className="absolute -inset-3 border border-vintage-ochre/20 rounded-full" />
            <div className="absolute -inset-5 border border-vintage-ochre/10 rounded-full" />
            <Image
              src={"/newest.png"}
              width={100}
              height={100}
              alt="Pizzicletta logo"
              className="relative z-10"
            />
          </div>

          {/* Tagline */}
          <p className="font-script text-vintage-ochre text-2xl mb-6 transform -rotate-1">
            Pizza Napoletana Autentica
          </p>

          {/* Decorative divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />
            <span className="text-vintage-ochre/60">✦</span>
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-vintage-ochre/40 to-transparent" />
          </div>

          {/* Social links */}
          <div className="flex items-center gap-6 mb-8">
            {settings?.facebookLink && (
              <a
                href={settings.facebookLink}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-full border border-vintage-ochre/30 text-vintage-cream/70 hover:text-vintage-cream hover:border-vintage-ochre hover:bg-vintage-ochre/10 transition-all duration-300"
                aria-label="Follow us on Facebook"
              >
                <IconBrandFacebookFilled size={22} />
                <span className="sr-only">Facebook page</span>
              </a>
            )}
            {settings?.instagramLink && (
              <a
                href={settings.instagramLink}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-full border border-vintage-ochre/30 text-vintage-cream/70 hover:text-vintage-cream hover:border-vintage-ochre hover:bg-vintage-ochre/10 transition-all duration-300"
                aria-label="Follow us on Instagram"
              >
                <IconBrandInstagram size={22} />
                <span className="sr-only">Instagram page</span>
              </a>
            )}
            {settings?.tiktokLink && (
              <a
                href={settings.tiktokLink}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-full border border-vintage-ochre/30 text-vintage-cream/70 hover:text-vintage-cream hover:border-vintage-ochre hover:bg-vintage-ochre/10 transition-all duration-300"
                aria-label="Follow us on TikTok"
              >
                <IconBrandTiktok size={22} />
                <span className="sr-only">TikTok page</span>
              </a>
            )}
          </div>

          {/* Terms and policies links */}
          <TermsAndPoliciesLinks className="w-full justify-center !text-vintage-cream/60 hover:!text-vintage-cream font-serif text-sm" />

          {/* Divider */}
          <div className="w-full max-w-md h-px bg-gradient-to-r from-transparent via-vintage-walnut to-transparent my-8" />

          {/* Copyright */}
          <div className="text-center">
            <p className="font-serif text-vintage-cream/50 text-sm">
              © {currentYear}{" "}
              <a
                href="https://pizzicletta.net/"
                className="hover:text-vintage-ochre transition-colors"
              >
                Pizzicletta™
              </a>
              . All Rights Reserved.
            </p>
            <p className="font-serif text-vintage-cream/30 text-xs mt-2">
              Kraków, Podgórze · Kalwaryjska 3
            </p>
          </div>
        </div>
      </div>

      {/* Decorative corner elements */}
      <div className="absolute bottom-4 left-4 w-8 h-8 border-l border-b border-vintage-ochre/20 hidden md:block" />
      <div className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-vintage-ochre/20 hidden md:block" />
    </footer>
  );
};
